import { useMemo, useState } from 'react'
import { CheckCircle2, XCircle, Building2, Eye, Clock, ShieldCheck } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import {
  useAdminPendingApprovals,
  useApproveRequestMutation,
  useRejectRequestMutation,
} from '../../hooks/use-admin'
import { EnterpriseTable, type Column } from '../../components/tables/enterprise-table'
import { ApprovalDialog } from '../../components/dialogs/approval-dialog'
import { UserDetailDrawer } from '../../components/dialogs/user-detail-drawer'
import type { ApprovalRequest } from '../../types/admin.types'

export function DealerRequestsPage() {
  const { data: pendingApprovals, isLoading } = useAdminPendingApprovals()
  const approveMutation = useApproveRequestMutation()
  const rejectMutation = useRejectRequestMutation()

  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null)
  const [dialogMode, setDialogMode] = useState<'approve' | 'reject'>('approve')
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [inspectRequest, setInspectRequest] = useState<ApprovalRequest | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Filter requests for DEALER type
  const dealerRequests = useMemo(() => {
    return (pendingApprovals ?? []).filter((req) => req.requestType === 'DEALER')
  }, [pendingApprovals])

  const handleOpenDialog = (request: ApprovalRequest, mode: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setDialogMode(mode)
    setIsDialogOpen(true)
  }

  const handleConfirmAction = async (
    id: number,
    notes?: string,
    riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH',
    planAssignment?: any,
  ) => {
    if (dialogMode === 'approve') {
      await approveMutation.mutateAsync({
        id,
        payload: { remarks: notes, riskLevel, ...(planAssignment ?? {}) },
      })
    } else {
      await rejectMutation.mutateAsync({ id, payload: { remarks: notes } })
    }
  }

  const handleInspectRequest = (req: ApprovalRequest) => {
    setInspectRequest(req)
    setIsDrawerOpen(true)
  }

  const columns: Column<ApprovalRequest>[] = [
    {
      key: 'applicant',
      header: 'Dealership / Applicant',
      sortable: true,
      sortValue: (r) => `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''}`,
      accessor: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500 font-display text-xs font-bold">
            <Building2 className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {r.user?.firstName} {r.user?.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{r.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'License Tier',
      accessor: () => <Badge variant="default">DEALER ENTERPRISE</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (r) => (
        <Badge variant={r.status === 'PENDING' ? 'warning' : 'default'} className="gap-1">
          <Clock className="size-3" /> {r.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Applied On',
      sortable: true,
      sortValue: (r) => new Date(r.createdAt).getTime(),
      accessor: (r) => (
        <span className="text-xs text-muted-foreground">
          {new Date(r.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="Business Onboarding"
        title="Dealer Approval Requests"
        description="Verify business registration, dealership credentials, and grant high-volume dealer access."
        align="left"
      />

      {/* Overview Metric */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500">
              <Building2 className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Pending Applications</p>
              <p className="font-display text-xl font-bold text-purple-400">
                {dealerRequests.length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
              <ShieldCheck className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Verification Protocol</p>
              <p className="font-display text-xl font-bold text-foreground">Strict Business SLA</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
              <Clock className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Target Review Time</p>
              <p className="font-display text-xl font-bold text-foreground">&lt; 24 hours</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <EnterpriseTable
        data={dealerRequests}
        columns={columns}
        isLoading={isLoading}
        searchPlaceholder="Search dealership by name or email..."
        searchableKeys={[(r) => `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''} ${r.user?.email ?? ''}`]}
        actions={(r) => (
          <div className="flex items-center justify-end gap-2">
            <Button
                variant="outline"
                size="sm"
                onClick={() => handleInspectRequest(r)}
                className="h-8 gap-1 text-xs"
              >
                <Eye className="size-3.5" /> License Details
              </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenDialog(r, 'reject')}
              disabled={rejectMutation.isPending}
              className="h-8 gap-1 text-xs text-red-500 hover:text-red-600 border-red-500/30 hover:bg-red-500/10"
            >
              <XCircle className="size-3.5" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={() => handleOpenDialog(r, 'approve')}
              disabled={approveMutation.isPending}
              className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              <CheckCircle2 className="size-3.5" /> Grant Approval
            </Button>
          </div>
        )}
        emptyTitle="No pending dealer requests"
        emptyDescription="All submitted dealership applications have been verified."
      />

      {/* Approval / Rejection Modal Dialog */}
      <ApprovalDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        request={selectedRequest}
        mode={dialogMode}
        onSubmit={handleConfirmAction}
        isPending={approveMutation.isPending || rejectMutation.isPending}
      />

      {/* User Inspection Drawer — shows full application data */}
      <UserDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        request={inspectRequest}
      />
    </div>
  )
}
